import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { logout } from "../redux/authReducer";
import {
  setCompanyAndGlobal,
  setMenuCompanyAndGlobal,
  setNoSavedEditedWineMenuPage,
  setSelectedCompany,
  setUserCompanyAndGlobal,
} from "../redux/globalReducer";
import { getAllCompany } from "../redux/locationReducer";
import { getMenusByFilter, getMenuTotalCount } from "../redux/menuReducer";
import {
  getProductsByFilter,
  getProductTotalCount,
} from "../redux/productReducer";
import { getUsersByFilter, getUserTotalCount } from "../redux/userReducer";
import { openSnackBar } from "../redux/snackBarReducer";

import SidebarLinkGroup from "./SidebarLinkGroup";
import Cart from "./Cart";
import Help from "./theme/DropdownHelp";
import Notifications from "./theme/DropdownNotifications";
import DropdownProfile from "./theme/DropdownProfile";
import SearchModal from "./theme/ModalSearch";

function Sidebar({ sidebarOpen, setSidebarOpen }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { pathname } = location;

  const trigger = useRef(null);
  const sidebar = useRef(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const { authState, locationState, globalState, productState} = useSelector(
    (state) => state
  );
  const { loggedIn, userInfo } = authState;
  const { companies } = locationState;
  const { noSavedEditedWineMenuPage } = globalState;
  const { getProductsByFilterState, getProductTotalCountState } = productState;

  const [isShow, setIsShow] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setIsShow(loggedIn);
  });

  useEffect(() => {
    dispatch(getAllCompany());
    dispatch(setCompanyAndGlobal(userInfo?.company));
    dispatch(
      setSelectedCompany(userInfo?.role === 0 ? "Global" : "Your Company")
    );
  }, [userInfo]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);

    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);

    return () => {
      document.removeEventListener("keydown", keyHandler);
      document.removeEventListener("click", clickHandler);

      return;
    };
  });

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector("body").classList.add("sidebar-expanded");
    } else {
      document.querySelector("body").classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  const setCompanyForProduct = async (company, isGlobal, position) => {
    if(!getProductsByFilterState && !getProductTotalCountState){
      await dispatch(setCompanyAndGlobal({ company, isGlobal, position }));
      await dispatch(
        getProductsByFilter({
          ...globalState.condition,
          company: company,
          isGlobal: isGlobal ? "global" : "company",
          currentPage: 0,
        })
      );
      await dispatch(
        getProductTotalCount({
          ...globalState.condition,
          company: company,
          isGlobal: isGlobal ? "global" : "company",
          currentPage: 0,
        })
      );
    }
  };

  const setCompanyForMenu = async (company, isGlobal, position) => {
    await dispatch(setMenuCompanyAndGlobal({ company, isGlobal, position }));
    await dispatch(
      getMenusByFilter({
        ...globalState.menuCondition,
        company: company,
        isGlobal: isGlobal ? "global" : "company",
        currentPage: 0,
      })
    );
    await dispatch(
      getMenuTotalCount({
        ...globalState.menuCondition,
        company: company,
        isGlobal: isGlobal ? "global" : "company",
        currentPage: 0,
      })
    );
  };

  const setCompanyForUser = async (company, isGlobal, position) => {
    await dispatch(setUserCompanyAndGlobal({ company, isGlobal, position }));
    await dispatch(
      getUsersByFilter({
        ...globalState.userCondition,
        company: company,
        isGlobal: isGlobal ? "global" : "company",
        currentPage: 0,
      })
    );
    await dispatch(
      getUserTotalCount({
        ...globalState.userCondition,
        company: company,
        isGlobal: isGlobal ? "global" : "company",
        currentPage: 0,
      })
    );
  };

  const navigateWithoutSave = (whereGO) => {
    if (noSavedEditedWineMenuPage === false) {
      if (
        window.confirm("Leave Site?\n\nChanges you made may not be saved.") ===
        true
      ) {
        dispatch(setNoSavedEditedWineMenuPage(true));
        navigate(whereGO);
      }
    } else {
      navigate(whereGO);
    }
  };

  return (
    <div>
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-slate-900 bg-opacity-30 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        aria-hidden="true"
      ></div>

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-screen overflow-y-scroll lg:overflow-y-auto no-scrollbar w-64 lg:w-20 lg:sidebar-expanded:!w-64 2xl:!w-64 shrink-0 bg-slate-800 p-4 transition-all duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-64'
          }`}
      >
        {/* Sidebar header */}
        <div className="flex justify-between mb-10 pr-3 sm:px-2">
          {/* Close button */}
          <button
            ref={trigger}
            className="lg:hidden text-slate-500 hover:text-slate-400"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
          {/* Logo */}
          <NavLink end to="/" className="mt-1 block">
            <svg
              width="192"
              height="24"
              viewBox="0 0 192 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_21_1054)">
                <path
                  d="M24.0077 23.4571H11.9423C11.4701 23.4571 10.9034 23.1162 10.6816 22.7014L0.248672 3.11686C-0.367324 1.96289 0.201446 1.01836 1.50941 1.01836H13.5747C14.047 1.01836 14.6137 1.35922 14.8355 1.77399L25.2705 21.3565C25.8865 22.5105 25.3177 23.455 24.0097 23.455L24.0077 23.4571ZM34.7917 1.01836H22.7223C22.25 1.01836 21.6812 1.35922 21.4615 1.77809L21.0961 2.46596C20.901 2.8335 20.901 3.43512 21.0961 3.80267L27.1308 15.2007C27.8248 16.5107 28.9603 16.5107 29.6543 15.2007L36.0545 3.11481C36.6643 1.96289 36.0976 1.01836 34.7938 1.01836H34.7917Z"
                  fill="url(#paint0_linear_21_1054)"
                />
                {sidebarExpanded && (
                  <path
                    d="M86.3234 2.66307V21.6522C86.3234 22.6357 85.5821 23.377 84.5986 23.377H83.457C82.4734 23.377 81.7322 22.6357 81.7322 21.6522V2.66307C81.7322 1.67953 82.4734 0.938279 83.457 0.938279H84.5986C85.5821 0.938279 86.3234 1.67953 86.3234 2.66307ZM143.114 8.01608V21.6501C143.114 22.6337 142.373 23.3749 141.389 23.3749H140.248C139.455 23.3749 138.821 22.8924 138.603 22.1922C137.359 23.0259 135.628 23.3749 133.18 23.3749H129.683C122.127 23.3749 120.998 19.2313 120.998 16.7591C120.998 14.4799 121.552 12.8927 122.741 11.7613C123.93 10.632 125.69 9.99138 128.445 9.68748L138.428 8.5828C138.465 8.57869 138.496 8.57253 138.521 8.56842C138.521 8.56226 138.521 8.55816 138.521 8.552C138.521 7.14753 138.371 6.40012 138.018 6.0531C137.613 5.65271 136.626 5.45969 134.999 5.45969H124.328C123.345 5.45969 122.604 4.71845 122.604 3.7349V2.66512C122.604 1.68158 123.345 0.940333 124.328 0.940333H135.036C140.624 0.940333 143.114 3.12301 143.114 8.02019V8.01608ZM138.521 14.0836V13.0221L128.991 14.0878C125.587 14.4512 125.587 15.1411 125.587 16.6524C125.587 17.3792 125.587 18.8884 129.679 18.8884H133.712C137.486 18.8884 138.519 17.8556 138.519 14.0816L138.521 14.0836ZM163.541 0.938279H159.756C158.085 0.938279 156.212 1.09639 154.744 1.97315C154.488 1.35305 153.888 0.938279 153.153 0.938279H152.011C151.028 0.938279 150.286 1.67953 150.286 2.66307V21.6522C150.286 22.6357 151.028 23.377 152.011 23.377H153.153C154.136 23.377 154.878 22.6357 154.878 21.6522V9.9462C154.878 7.93395 155.165 6.68553 155.759 6.12702C156.262 5.65271 157.171 5.45969 158.9 5.45969H163.541C164.524 5.45969 165.265 4.71845 165.265 3.7349V2.66512C165.265 1.68158 164.524 0.940333 163.541 0.940333V0.938279ZM192 9.94415V14.2623C192 16.7489 191.798 19.2909 190.332 21.0485C188.99 22.6583 186.858 23.377 183.423 23.377H177.497C174.062 23.377 171.93 22.6583 170.588 21.0485C169.122 19.2909 168.92 16.7489 168.92 14.2623V9.94415C168.92 5.80876 169.764 4.24618 170.592 3.26264C171.924 1.67542 174.119 0.938279 177.499 0.938279H183.425C186.805 0.938279 189 1.67748 190.332 3.26264C191.805 5.01412 192.004 7.6239 192.004 9.94415H192ZM186.729 6.15166C186.275 5.66503 185.275 5.45764 183.386 5.45764H177.532C175.643 5.45764 174.643 5.66503 174.189 6.15166C173.713 6.66089 173.51 7.76352 173.51 9.83738V14.3711C173.51 16.5209 173.711 17.6585 174.181 18.1698C174.622 18.6502 175.624 18.8556 177.53 18.8556H183.384C185.289 18.8556 186.291 18.6502 186.733 18.1698C187.203 17.6564 187.404 16.5209 187.404 14.3711V9.83738C187.404 7.76352 187.201 6.66089 186.725 6.15166H186.729ZM98.4688 6.15166C98.9226 5.66503 99.9225 5.45764 101.812 5.45764H107.666C109.321 5.45764 110.286 5.61575 110.793 5.97303C111.115 6.19889 111.464 6.63419 111.612 8.13928C111.696 8.99346 112.409 9.6382 113.269 9.6382H114.524C114.992 9.6382 115.442 9.43903 115.758 9.09202C116.072 8.74501 116.228 8.28096 116.183 7.81486C115.706 2.8643 113.329 0.936226 107.701 0.936226H101.775C98.3949 0.936226 96.1999 1.67542 94.8673 3.26059C94.0418 4.24413 93.1959 5.80671 93.1959 9.9421V14.2602C93.1959 16.7468 93.3971 19.2888 94.8632 21.0465C96.206 22.6563 98.3374 23.3749 101.773 23.3749H107.698C113.308 23.3749 115.684 21.4469 116.175 16.5025C116.222 16.0364 116.066 15.5703 115.752 15.2232C115.435 14.8742 114.986 14.675 114.516 14.675H113.263C112.401 14.675 111.688 15.3197 111.606 16.176C111.46 17.6729 111.119 18.1061 110.807 18.3299C110.3 18.6934 109.331 18.8556 107.662 18.8556H101.807C99.902 18.8556 98.9 18.6502 98.4585 18.1698C97.9883 17.6564 97.7871 16.5209 97.7871 14.3711V9.83738C97.7871 7.76352 97.9904 6.66089 98.4667 6.15166H98.4688ZM75.2252 3.30165L75.2108 3.32835L65.0797 22.3421C64.745 22.9992 64.127 23.3749 63.3816 23.3749H61.6692C60.9238 23.3749 60.3058 22.9992 59.9711 22.3401L49.8256 3.30165C49.5525 2.75547 49.5648 2.16617 49.8605 1.68774C50.1521 1.21753 50.6818 0.936226 51.2835 0.936226H52.6037C53.3491 0.936226 53.9671 1.31404 54.3018 1.9711L62.5254 17.5004L70.7489 1.97315C71.0836 1.31404 71.7037 0.938279 72.4491 0.938279H73.8043C74.4018 0.938279 74.911 1.20726 75.2005 1.67337C75.4962 2.14974 75.5044 2.7452 75.2252 3.30165Z"
                    fill="white"
                  />
                )}
              </g>
              <defs>
                <linearGradient
                  id="paint0_linear_21_1054"
                  x1="6.26283"
                  y1="-0.293708"
                  x2="19.9174"
                  y2="23.5248"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#6366F1" />
                  <stop offset="0.997048" stopColor="#1E24B3" />
                </linearGradient>
                <clipPath id="clip0_21_1054">
                  <rect
                    width="192"
                    height="22.5188"
                    fill="white"
                    transform="translate(0 0.938286)"
                  />
                </clipPath>
              </defs>
            </svg>
            <div className=""></div>
          </NavLink>
        </div>

        {/* Links */}




        <div className="space-y-8 flex flex-col flex-1">
          {/* Pages group */}
          <div className="flex-1">
            <h3 className="text-xs uppercase text-slate-500 font-semibold pl-3">
              <span
                className="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center w-6"
                aria-hidden="true"
              >
                •••
              </span>
              <span className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                {t("pages")}
              </span>
            </h3>
            <ul className="mt-3">

                <>
                  <li
                    className={`px-3 py-2 rounded-sm mb-0.5 last:mb-0 ${pathname.includes("products") && "bg-slate-900"
                      }`}
                  >
                    <NavLink
                      end
                      to="/products"
                      className={`block text-slate-200 hover:text-white truncate transition duration-150 ${pathname.includes("products") && "hover:text-slate-200"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="grow flex items-center">
                          <svg className="shrink-0 h-6 w-6" stroke-width="1.5px"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <defs/><title>layout-module-1</title>
                            <rect className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("products")) &&
                                "!text-indigo-500"
                                }`} x="0.75" y="0.747" width="9" height="9" rx="1.5" ry="1.5" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/>
                            <rect className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("products")) &&
                                "!text-indigo-500"
                                }`} x="0.75" y="14.247" width="9" height="9" rx="1.5" ry="1.5" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/>
                            <rect className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("products")) &&
                                "!text-indigo-500"
                                }`} x="14.25" y="0.747" width="9" height="9" rx="1.5" ry="1.5" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/>
                            <rect className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("products")) &&
                                "!text-indigo-500"
                                }`} x="14.25" y="14.247" width="9" height="9" rx="1.5" ry="1.5" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                            {t("products")}
                          </span>
                        </div>
                      </div>
                    </NavLink>
                  </li>
                  <li
                    className={`px-3 py-2 rounded-sm mb-0.5 last:mb-0 ${pathname.includes("menu") && "bg-slate-900"
                      }`}
                  >
                    <NavLink
                      end
                      to="/menu"
                      className={`block text-slate-200 hover:text-white truncate transition duration-150 ${pathname.includes("menu") && "hover:text-slate-200"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="grow flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="shrink-0 h-6 w-6"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24" stroke-width="1.5px">
                            <g><line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("menu")) &&
                                "!text-indigo-500"
                                }`} x1="3.75" y1="10.25" x2="20.25" y2="10.25" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("menu")) &&
                                "!text-indigo-500"
                                }`} x1="3.75" y1="6.5" x2="20.25" y2="6.5" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("menu")) &&
                                "!text-indigo-500"
                                }`} x1="3.75" y1="2.75" x2="20.25" y2="2.75" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/>
                            <path className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("menu")) &&
                                "!text-indigo-500"
                                }`} d="M22.5,14H15a3,3,0,0,1-6,0H1.5a.76.76,0,0,0-.75.75v5a1.5,1.5,0,0,0,1.5,1.5h19.5a1.5,1.5,0,0,0,1.5-1.5v-5A.76.76,0,0,0,22.5,14Z" fill="none" stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round"/></g></svg>
                          <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                            {t("wine_menus")}
                          </span>
                        </div>
                      </div>
                    </NavLink>
                  </li>
                  {
                    userInfo?.role == 0 ?
                    <>
                      <SidebarLinkGroup
                        activecondition={pathname.includes("user") || pathname.includes("company")}
                      >
                        {(handleClick, open) => {
                          return (
                            <React.Fragment>
                              <a
                                href="#0"
                                className={`block text-slate-200 hover:text-white truncate transition duration-150 ${pathname.includes("company") &&
                                  "hover:text-slate-200"
                                  }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  sidebarExpanded
                                    ? handleClick()
                                    : setSidebarExpanded(true);
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none" stroke-width="1.5">
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M19.75 18.25C21.1307 18.25 22.25 17.1307 22.25 15.75C22.25 14.3693 21.1307 13.25 19.75 13.25C18.3693 13.25 17.25 14.3693 17.25 15.75C17.25 17.1307 18.3693 18.25 19.75 18.25Z"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M23.25 23.25C23.25 21.35 21.65 19.75 19.75 19.75C17.85 19.75 16.25 21.35 16.25 23.25"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M12.75 18.25C14.1307 18.25 15.25 17.1307 15.25 15.75C15.25 14.3693 14.1307 13.25 12.75 13.25C11.3693 13.25 10.25 14.3693 10.25 15.75C10.25 17.1307 11.3693 18.25 12.75 18.25Z"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M16.25 23.25C16.25 21.35 14.65 19.75 12.75 19.75C10.85 19.75 9.25 21.35 9.25 23.25"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.65 23.15H0.75"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M1.75 23.15V1.75901C1.75 1.1536 2.15 0.75 2.75 0.75H14.25C14.85 0.75 15.25 1.1536 15.25 1.75901V10.6383"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 4.94995H12.05"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 8.94995H12.05"/>
                                      <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 12.95H7.65005"/>
                                      <path className={`stroke-current text-slate-600 ${(pathname.includes("company") || pathname.includes("user")) &&
                                          "!text-indigo-500"
                                          }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 16.95H7.05005"/></svg>
                                    <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                      {t("companies&users")}
                                    </span>
                                  </div>
                                  {/* Icon */}
                                  <div className="flex shrink-0 ml-2">
                                    <svg
                                      className={`w-3 h-3 shrink-0 ml-1 fill-current text-slate-400 ${open && "rotate-180"
                                        }`}
                                      viewBox="0 0 12 12"
                                    >
                                      <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                                    </svg>
                                  </div>
                                </div>
                              </a>
                              <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                                <ul className={`pl-9 mt-1 ${!open && "hidden"}`}>
                                  <li className="mb-1 last:mb-0">
                                    <NavLink
                                      end
                                      to="/company"
                                      className={({ isActive }) =>
                                        "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " +
                                        (isActive ? "!text-indigo-500" : "")
                                      }
                                    >
                                      <span
                                        onClick={() =>
                                          navigateWithoutSave("company")
                                        }
                                        className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200"
                                      >
                                        {t("global")}
                                      </span>
                                    </NavLink>
                                  </li>
                                  {companies?.length > 0 &&
                                    companies.map((data, index) => (
                                      <li
                                        key={`company-user-${index}`}
                                        className="mb-1 last:mb-0"
                                      >
                                        <NavLink
                                          end
                                          to="/user"
                                          className={({ isActive }) =>
                                            "block text-slate-400 hover:text-slate-200 transition duration-150 truncate " +
                                            (isActive ? "!text-indigo-500" : "")
                                          }
                                        >
                                          <span
                                            onClick={() => {
                                              navigateWithoutSave("user");
                                              setCompanyForUser(
                                                data._id,
                                                false,
                                                data.name
                                              );
                                            }}
                                            className="text-sm font-medium lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200"
                                          >
                                            {data.name}
                                          </span>
                                        </NavLink>
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            </React.Fragment>
                          );
                        }}
                      </SidebarLinkGroup>
                    </> :
                    <>
                      <li
                        className={`px-3 py-2 rounded-sm mb-0.5 last:mb-0 ${pathname.includes("user") && "bg-slate-900"}`}
                      >
                        <NavLink
                          end
                          to="/user"
                          className={`block text-slate-200 hover:text-white truncate transition duration-150 ${pathname.includes("user") && "hover:text-slate-200"}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="grow flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none" stroke-width="1.5px">
                                <defs/><title>conversation-chat-text</title>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M19.75 18.25C21.1307 18.25 22.25 17.1307 22.25 15.75C22.25 14.3693 21.1307 13.25 19.75 13.25C18.3693 13.25 17.25 14.3693 17.25 15.75C17.25 17.1307 18.3693 18.25 19.75 18.25Z"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M23.25 23.25C23.25 21.35 21.65 19.75 19.75 19.75C17.85 19.75 16.25 21.35 16.25 23.25"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M12.75 18.25C14.1307 18.25 15.25 17.1307 15.25 15.75C15.25 14.3693 14.1307 13.25 12.75 13.25C11.3693 13.25 10.25 14.3693 10.25 15.75C10.25 17.1307 11.3693 18.25 12.75 18.25Z"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M16.25 23.25C16.25 21.35 14.65 19.75 12.75 19.75C10.85 19.75 9.25 21.35 9.25 23.25"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.65 23.15H0.75"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M1.75 23.15V1.75901C1.75 1.1536 2.15 0.75 2.75 0.75H14.25C14.85 0.75 15.25 1.1536 15.25 1.75901V10.6383"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 4.94995H12.05"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 8.94995H12.05"/>
                                <path className={`stroke-current text-slate-400 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 12.95H7.65005"/>
                                <path className={`stroke-current text-slate-600 ${(pathname.includes("company") || pathname.includes("user")) &&
                                    "!text-indigo-500"
                                    }`} stroke="#6366F1" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" d="M5.05005 16.95H7.05005"/></svg>
                              <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                                {t("companies&users")}
                              </span>
                            </div>
                          </div>
                        </NavLink>
                      </li>
                      
                    </>
                  }
                  <li
                    className={`px-3 py-2 rounded-sm mb-0.5 last:mb-0 ${pathname.includes("producer") && "bg-slate-900"}`}
                  >
                    <NavLink
                      end
                      to="/producer"
                      className={`block text-slate-200 hover:text-white truncate transition duration-150 ${pathname.includes("producer") && "hover:text-slate-200"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="grow flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" stroke-width="1.5px">
                            <defs/>
                            <title>harvest-farmer-2</title>
                            <path className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} d="M9.418,4.375a2.5,2.5,0,0,1-5,0v-1a2.5,2.5,0,1,1,5,0Z" stroke-miterlimit="10" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="2.08" y1="3.62" x2="12.168" y2="3.625" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <path className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} d="M4.861,9.125a4,4,0,0,0-4,4.025L.88,16.134a1.5,1.5,0,0,0,1.5,1.491h.01a1.5,1.5,0,0,0,1.456-1.189v5.189a1.5,1.5,0,0,0,3,0,1.5,1.5,0,0,0,3,0V16.436A1.5,1.5,0,0,0,11.3,17.625h.01a1.5,1.5,0,0,0,1.5-1.491l.018-2.984a4,4,0,0,0-4-4.025Z" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="3.846" y1="16.875" x2="3.846" y2="12.875" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="9.846" y1="16.875" x2="9.846" y2="12.875" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="6.846" y1="21.875" x2="6.846" y2="17.875" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <path className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} d="M15.468,12.518H18.8a5.039,5.039,0,0,1,1.719.3l1.8.654a1.258,1.258,0,0,1,.828,1.182v4.525a1.257,1.257,0,0,1-.828,1.181l-1.8.654a5.04,5.04,0,0,1-1.719.3H15.468a5.03,5.03,0,0,1-1.591-.258l-1.5-.5" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <path className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} d="M12.811,6.844l1.066-.355a5.03,5.03,0,0,1,1.591-.258H18.8a5.016,5.016,0,0,1,1.719.3l1.8.653a1.258,1.258,0,0,1,.828,1.182v2.263" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="14.652" y1="19.333" x2="14.652" y2="23.125" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="19.681" y1="12.596" x2="19.681" y2="23.125" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="14.652" y1="6.298" x2="14.652" y2="9.06" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                            <line className={`stroke-current text-slate-400 ${(pathname === "/" ||
                                  pathname.includes("producer")) &&
                                "!text-indigo-500"
                                }`} x1="19.681" y1="6.31" x2="19.681" y2="9.06" stroke-linecap="round" stroke-linejoin="round" fill="none" stroke="#000000"/>
                          </svg>
                          <span className="text-sm font-medium ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                            {t("producer")}
                          </span>
                        </div>
                      </div>
                    </NavLink>
                  </li>
                </>
            </ul>
          </div>
          {/* More group */}
          <div>
            <h3 className="text-xs uppercase text-slate-500 font-semibold pl-3">
              <span
                className="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center w-6"
                aria-hidden="true"
              >
                •••
              </span>
              <span className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                {t("more")}
              </span>
            </h3>
            <ul className="mt-3">
              <SidebarLinkGroup>
                {(handleClick, open) => {
                  return (
                    <a
                      href="#0"
                      className={`lg:hidden lg:sidebar-expanded:block 2xl:block block text-slate-200 hover:text-white truncate transition duration-150 ${open && "hover:text-slate-200"
                        }`}
                      onClick={(e) => {
                        e.preventDefault();
                        sidebarExpanded
                          ? handleClick()
                          : setSidebarExpanded(true);
                      }}
                    >
                      {t("contact")}
                    </a>
                  );
                }}
              </SidebarLinkGroup>
              <SidebarLinkGroup>
                {(handleClick, open) => {
                  return ( 
                    <>

                      <a
                        href="#0"
                        className={`lg:hidden lg:sidebar-expanded:block 2xl:block block text-slate-200  hover:text-white truncate transition duration-150 ${open && "hover:text-slate-200"
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          sidebarExpanded
                            ? handleClick()
                            : setSidebarExpanded(true);
                        }}
                      >
                        {t("privacy_policy")}
                      </a>
                    </>
                  );
                }}
              </SidebarLinkGroup>
              <SidebarLinkGroup>
                {(handleClick, open) => {
                  return (
                    <a
                      href="#0"
                      className={`lg:hidden lg:sidebar-expanded:block 2xl:block block text-slate-200 hover:text-white truncate transition duration-150 ${open && "hover:text-slate-200"
                        }`}
                      onClick={(e) => {
                        e.preventDefault();
                        sidebarExpanded
                          ? handleClick()
                          : setSidebarExpanded(true);
                      }}
                    >
                      {t("imprint")}
                    </a>
                  );
                }}
              </SidebarLinkGroup>
            </ul>
          </div>
        </div>

        {/* Expand / collapse button */}
        <div className="pt-3 hidden lg:inline-flex 2xl:hidden justify-end mt-auto">
          <div className="px-3 py-2">
            <button onClick={() => setSidebarExpanded(!sidebarExpanded)}>
              <span className="sr-only">{t("expand_collapse")}</span>
              <svg className="w-6 h-6 fill-current sidebar-expanded:rotate-180" viewBox="0 0 24 24">
                <path className="text-slate-400" d="M19.586 11l-5-5L16 4.586 23.414 12 16 19.414 14.586 18l5-5H7v-2z" />
                <path className="text-slate-600" d="M3 23H1V1h2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;


